import { FormEvent, useCallback, useState } from 'react';
import creditCardType from 'credit-card-type';
import { noop } from '../lib/noop';

type FormElement = HTMLInputElement & HTMLSelectElement & HTMLTextAreaElement;
type FormElementNodeName = 'INPUT' | 'SELECT' | 'TEXTAREA';
type FormElementNodeNames = {
  [TNodeName in FormElementNodeName]: boolean;
};
type FormElements = {
  [elementName: string]: FormElement;
};
type FormValues = {
  [elementName: string]: string | boolean;
};

type ValidationType =
  | 'email'
  | 'maxLength'
  | 'minLength'
  | 'pattern'
  | 'required'
  | 'creditCardNumber'
  | 'sameAs'
  | 'differentFrom'
  | 'maxAmount';

export type FormErrors = {
  [elementName: string]: {
    element: FormElement;
    message?: string;
    type: ValidationType;
  };
};

type Validation<TValue> = {
  message?: string;
  value: TValue;
};

export type Validations = {
  [elementName: string]: {
    email?: Validation<boolean>;
    maxLength?: Validation<number>;
    minLength?: Validation<number>;
    pattern?: Validation<RegExp>;
    required?: Validation<boolean>;
    creditCardNumber?: Validation<boolean>;
    maxAmount?: Validation<number | undefined>;
    sameAs?: Validation<string>;
    differentFrom?: Validation<string>;
  };
};
type Props = {
  focusOnError?: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSubmit?: Function;
  validations?: Validations;
};

const formElementNodeNames: FormElementNodeNames = { INPUT: true, SELECT: true, TEXTAREA: true };

const getFormElementArray = (form: HTMLFormElement): FormElement[] => {
  return Array.from(form.elements).filter(el => {
    return formElementNodeNames[el.nodeName as FormElementNodeName];
  }) as FormElement[];
};

const getElementName = (element: FormElement): string => {
  return element.name || element.id;
};

const getElementValue = (element: FormElement): string | boolean => {
  return element.type === 'checkbox' ? element.checked : element.value;
};

const getFormElements = (formElementArray: FormElement[]): FormElements => {
  return formElementArray.reduce(
    (formElements: FormElements, element: FormElement): FormElements => {
      const elementName = getElementName(element);

      if (elementName) {
        formElements[elementName] = element;
      }

      return formElements;
    },
    {}
  );
};

const getFormValues = (formElements: FormElements): FormValues => {
  return Object.keys(formElements).reduce((formValues: FormValues, name: string): FormValues => {
    const element = formElements[name];

    formValues[name] = getElementValue(element);

    return formValues;
  }, {});
};

const validateCreditCardNumber = (number: string) =>
  creditCardType(number).some(type =>
    type.lengths?.some(length => number.replace(/ /g, '').length === length)
  );

const validateFormElements = (elements: FormElements, validations: Validations): FormErrors => {
  return Object.keys(validations).reduce((errors: FormErrors, name: string): FormErrors => {
    const validation = validations[name];
    const element = elements[name];

    if (!validation || !element) {
      return errors;
    }

    const elementName = getElementName(element);

    if (validation.required?.value && !element.value) {
      errors[elementName] = {
        element,
        message: validation.required.message,
        type: 'required'
      };

      return errors;
    }

    if (!element.value) {
      return errors;
    }

    if (validation.minLength?.value && element.value.length < validation.minLength.value) {
      errors[elementName] = {
        element,
        message: validation.minLength.message,
        type: 'minLength'
      };

      return errors;
    }

    if (validation.maxLength?.value && element.value.length > validation.maxLength.value) {
      errors[elementName] = {
        element,
        message: validation.maxLength.message,
        type: 'maxLength'
      };

      return errors;
    }

    if (validation.email?.value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(element.value)) {
      errors[elementName] = {
        element,
        message: validation.email.message,
        type: 'email'
      };

      return errors;
    }

    if (validation.pattern?.value && !validation.pattern.value.test(element.value)) {
      errors[elementName] = {
        element,
        message: validation.pattern.message,
        type: 'pattern'
      };

      return errors;
    }

    if (validation.creditCardNumber?.value && !validateCreditCardNumber(element.value)) {
      errors[elementName] = {
        element,
        message: validation.creditCardNumber.message,
        type: 'creditCardNumber'
      };

      return errors;
    }

    if (validation.maxAmount?.value && Number(element.value) > Number(validation.maxAmount.value)) {
      errors[elementName] = {
        element,
        message: validation.maxAmount.message,
        type: 'maxAmount'
      };

      return errors;
    }

    if (validation.differentFrom?.value && validation.differentFrom.value === element.value) {
      errors[elementName] = {
        element,
        message: validation.differentFrom.message,
        type: 'differentFrom'
      };
    }

    if (validation.sameAs?.value && validation.sameAs.value !== element.value) {
      errors[elementName] = {
        element,
        message: validation.sameAs.message,
        type: 'sameAs'
      };
    }

    return errors;
  }, {});
};

const getNumErrors = (formErrors: FormErrors): number => {
  return Object.keys(formErrors).length;
};

const focusFirstError = (formErrors: FormErrors, formElementArray: FormElement[]): void => {
  formElementArray.some(element => {
    const elementName = getElementName(element);

    if (formErrors[elementName]) {
      element.focus();

      return true;
    }

    return false;
  });
};

export const useForm = ({ focusOnError = true, onSubmit = noop, validations = {} }: Props = {}) => {
  const [errors, setErrors] = useState<FormErrors>({});

  const handleSubmit = useCallback(
    (evt: FormEvent) => {
      evt.preventDefault();

      const form = evt.target as HTMLFormElement;
      const formElementArray = getFormElementArray(form);
      const formElements = getFormElements(formElementArray);
      const formValues = getFormValues(formElements);
      const formErrors = validateFormElements(formElements, validations);
      const numErrors = getNumErrors(formErrors);

      setErrors(formErrors);

      if (numErrors === 0) {
        onSubmit(formValues, formElements, form);
      } else if (focusOnError) {
        focusFirstError(formErrors, formElementArray);
      }
    },
    [focusOnError, onSubmit, validations]
  );

  const numErrors = getNumErrors(errors);
  const hasErrors = numErrors > 0;

  return { errors, handleSubmit, hasErrors, numErrors };
};
